/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file jackpotSlice.js
 * @description Redux slice for managing jackpot related state and operations
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";

/**
 * @function getInitialState
 * @description Creates the initial state object for the jackpot slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = (jackpots = []) => ({
  error: null,
  jackpots,
  loading: false,
});

/**
 * @function getJackpots
 * @description Async thunk for get jackpots operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getJackpots = createAsyncThunk("jackpots/loadJackpots", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    const result = await axios.get(`/player/jackpotbet/list?originId=${originId}&lineId=${lineId}`);

    return {
      jackpots: result.data,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain country details", // serializable (err.response.data)
      name: "Country list Fetch Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @constant jackpotSlice
 * @description Redux slice for jackpot management
 */
const jackpotSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getJackpots.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getJackpots.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(getJackpots.fulfilled, (state, action) => {
        state.jackpots = action.payload.jackpots;
        state.loading = false;
        state.error = null;
      });
  },
  initialState: getInitialState(),
  name: "jackpots",
  reducers: {},
});
const { reducer } = jackpotSlice;
export default reducer;
